/**
 * Route preview for an activity log.
 *
 * Web counterpart of the native GenericActivityAnalyticsRoute (react-native-maps).
 * Rather than pulling in a full mapping dependency, the recorded GPS track is
 * projected with a simple equirectangular projection and drawn as an SVG
 * polyline — enough to convey the route shape. Renders nothing when the log has
 * no coordinates (mirrors the native null return).
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useActivityRoute } from '../../../Hooks/useActivityQueries';
import { Theme } from '../../../Theme/Theme';

const MapContainer = styled.div`
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  background: ${Theme.Colors.lightestGrey};
  border: 1px solid ${Theme.Colors.lighterGrey};

  & > svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

export type ActivityLogRouteMapProps = {
  activityLogId?: string;
};

const VIEWBOX = 300;
const PADDING = 16;

export const ActivityLogRouteMap: React.FC<ActivityLogRouteMapProps> = observer(({ activityLogId }) => {
  const { data: route } = useActivityRoute(activityLogId);

  const points = React.useMemo(() => {
    const coords = Array.isArray(route)
      ? route
          .map((l: any) => ({ lat: Number(l.lat), lon: Number(l.lon) }))
          .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lon))
      : [];
    if (coords.length < 2) return undefined;

    const lats = coords.map((c) => c.lat);
    const lons = coords.map((c) => c.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    // Equirectangular projection corrected for latitude, then fit to the viewbox
    // while preserving aspect ratio so the track isn't stretched.
    const midLatRad = ((minLat + maxLat) / 2) * (Math.PI / 180);
    const spanX = Math.max((maxLon - minLon) * Math.cos(midLatRad), 1e-6);
    const spanY = Math.max(maxLat - minLat, 1e-6);
    const scale = (VIEWBOX - 2 * PADDING) / Math.max(spanX, spanY);
    const offsetX = (VIEWBOX - spanX * scale) / 2;
    const offsetY = (VIEWBOX - spanY * scale) / 2;

    return coords
      .map((c) => {
        const x = offsetX + (c.lon - minLon) * Math.cos(midLatRad) * scale;
        // SVG y grows downward, latitude grows upward -> invert
        const y = offsetY + (maxLat - c.lat) * scale;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  }, [route]);

  if (!points) return null;

  const firstPoint = points.split(' ')[0].split(',');
  const lastPointStr = points.split(' ');
  const lastPoint = lastPointStr[lastPointStr.length - 1].split(',');

  return (
    <MapContainer>
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Route">
        <polyline
          points={points}
          fill="none"
          stroke={Theme.Colors.primaryDarkest}
          strokeWidth={5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={firstPoint[0]} cy={firstPoint[1]} r={6} fill={Theme.Colors.primary} />
        <circle cx={lastPoint[0]} cy={lastPoint[1]} r={6} fill={Theme.Colors.error} />
      </svg>
    </MapContainer>
  );
});
